import { useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { CreditCard } from 'lucide-react';
import { Button } from '@/components/ui';
import { formatCurrency, type Currency } from '@/utils/currency';
import { STRIPE_PUBLISHABLE_KEY } from '@/config/api';
import { useStripeElementsAppearance } from '@/hooks/useStripeElementsAppearance';

interface PublicStripePaymentConfirmInnerProps {
  amountCents: number;
  currency: string;
  onSuccess: () => void;
  onCancel: () => void;
}

/** Renders inside <Elements options={{ clientSecret }}> — confirms a PaymentIntent or subscription's first invoice. */
function PublicStripePaymentConfirmInner({
  amountCents,
  currency,
  onSuccess,
  onCancel,
}: PublicStripePaymentConfirmInnerProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message || 'Please check the payment details.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: `${window.location.origin}${window.location.pathname}` },
        redirect: 'if_required',
      });

      if (paymentError) {
        setError(paymentError.message || 'Payment failed');
      } else if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
        onSuccess();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
      <div className="rounded-md border border-gray-200 p-3 dark:border-gray-700">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>
      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading} className="flex-1">
          Back
        </Button>
        <Button type="button" onClick={handleConfirm} disabled={loading || !stripe} isLoading={loading} className="flex-1">
          {!loading && (
            <>
              <CreditCard className="mr-2 h-4 w-4" aria-hidden />
              Pay {formatCurrency(amountCents / 100, currency as Currency)}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

interface PublicStripePaymentConfirmProps {
  clientSecret: string;
  stripeConnectAccountId: string;
  amountCents: number;
  currency: string;
  onSuccess: () => void;
  onCancel: () => void;
}

/** Public (unauthenticated-page-safe) Stripe Payment Element confirmation for a connected-account charge. */
export function PublicStripePaymentConfirm({
  clientSecret,
  stripeConnectAccountId,
  amountCents,
  currency,
  onSuccess,
  onCancel,
}: PublicStripePaymentConfirmProps) {
  const { appearance } = useStripeElementsAppearance();
  const stripePromise = useMemo(() => {
    if (!STRIPE_PUBLISHABLE_KEY) return null;
    return loadStripe(STRIPE_PUBLISHABLE_KEY, { stripeAccount: stripeConnectAccountId });
  }, [stripeConnectAccountId]);

  if (!STRIPE_PUBLISHABLE_KEY || !clientSecret || !stripePromise) {
    return (
      <p className="text-sm text-red-600 dark:text-red-400">
        Payments are not configured for this club yet. Please contact the club directly.
      </p>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
      <PublicStripePaymentConfirmInner
        amountCents={amountCents}
        currency={currency}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
}
